// Utilidades para detectar registros duplicados antes de guardar.
// Se usan en DataContext para pacientes, doctores, contactos, empresas y citas.

export const normalizeEmail = (email?: string | null): string =>
(email || '').trim().toLowerCase();

export const normalizePhone = (phone?: string | null): string =>
(phone || '').replace(/\D/g, '');

export const normalizeName = (name?: string | null): string =>
(name || '').
trim().
toLowerCase().
normalize('NFD').
replace(/[\u0300-\u036f]/g, '') // quita acentos
.replace(/\s+/g, ' ');

interface BasicPersonLike {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  cedula?: string;
}

export interface DuplicateMatch<T> {
  match: T;
  reason: string;
}

/**
 * Busca un duplicado dentro de una lista de personas (pacientes, doctores, contactos)
 * comparando email, teléfono y cédula normalizados. Ignora campos vacíos.
 * `excludeId` permite omitir el propio registro al editar.
 */
export function findDuplicatePerson<T extends BasicPersonLike>(
list: T[],
candidate: {name?: string;email?: string;phone?: string;cedula?: string;},
excludeId?: string)
: DuplicateMatch<T> | null {
  const email = normalizeEmail(candidate.email);
  const phone = normalizePhone(candidate.phone);
  const cedula = normalizePhone(candidate.cedula); // cédulas dominicanas solo tienen dígitos y guiones

  for (const item of list) {
    if (excludeId && item.id === excludeId) continue;

    const itemEmail = normalizeEmail(item.email);
    const itemPhone = normalizePhone(item.phone);
    const itemCedula = normalizePhone(item.cedula);

    if (cedula && itemCedula && cedula === itemCedula) {
      return { match: item, reason: `Ya existe un registro con la misma cédula (${item.name})` };
    }
    if (email && itemEmail && email === itemEmail) {
      return { match: item, reason: `Ya existe un registro con el mismo correo electrónico (${item.name})` };
    }
    if (phone && itemPhone && phone.length >= 7 && phone === itemPhone) {
      return { match: item, reason: `Ya existe un registro con el mismo teléfono (${item.name})` };
    }
  }
  return null;
}

interface BasicCompanyLike {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
}

export function findDuplicateCompany<T extends BasicCompanyLike>(
list: T[],
candidate: {name?: string;email?: string;phone?: string;},
excludeId?: string)
: DuplicateMatch<T> | null {
  const email = normalizeEmail(candidate.email);
  const phone = normalizePhone(candidate.phone);
  const name = normalizeName(candidate.name);

  for (const item of list) {
    if (excludeId && item.id === excludeId) continue;
    const itemEmail = normalizeEmail(item.email);
    const itemPhone = normalizePhone(item.phone);
    const itemName = normalizeName(item.name);

    if (email && itemEmail && email === itemEmail) {
      return { match: item, reason: `Ya existe una empresa con el mismo correo (${item.name})` };
    }
    if (phone && itemPhone && phone.length >= 7 && phone === itemPhone) {
      return { match: item, reason: `Ya existe una empresa con el mismo teléfono (${item.name})` };
    }
    if (name && itemName && name === itemName) {
      return { match: item, reason: `Ya existe una empresa con el mismo nombre (${item.name})` };
    }
  }
  return null;
}

interface BasicAppointmentLike {
  id: string;
  doctorId: string;
  date: string;
  time: string;
  status: string;
}

/**
 * Detecta si un doctor ya tiene una cita programada en la misma fecha/hora
 * (evita doble reserva). Ignora citas canceladas.
 */
export function findAppointmentConflict<T extends BasicAppointmentLike>(
list: T[],
candidate: {doctorId: string;date: string;time: string;},
excludeId?: string)
: T | null {
  for (const item of list) {
    if (excludeId && item.id === excludeId) continue;
    if (item.status === 'cancelada') continue;
    if (
    item.doctorId === candidate.doctorId &&
    item.date === candidate.date &&
    item.time === candidate.time)
    {
      return item;
    }
  }
  return null;
}